import { HttpClient, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import { AuthSession, AuthUser, confirmResetPassword, confirmSignIn, confirmSignUp, fetchAuthSession, getCurrentUser, resendSignUpCode, resetPassword, signIn, signOut, signUp } from 'aws-amplify/auth';
import * as moment from 'moment';
import { from, Observable, ReplaySubject } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Environment } from '../../environments/environment';
import { ApplicationState } from '../types/ApplicationState';
import { oneLine } from '../utils/one-line-converter';
import { LoadingService } from './loading.service';
import { NetworkConnectivityService } from './network-connectivity.service';


interface ICognitoSignUpData {
  phone_number: string;
  password: string;
  given_name: string;
  family_name: string;
  birthdate: string;
  gender: string;
  'custom:sitecode': string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  refreshAuth$ = new ReplaySubject<{ redirectTo: string }>();

  constructor(public http: HttpClient,
    public loadingService: LoadingService,
    private network: NetworkConnectivityService
  ) {
    this.refreshAuth$.next({ redirectTo: '' });
    this.refreshAuth$.subscribe(() => {
      this.forceRefreshToken();
    });
  }

  static AUTH_ERROR: any /* BackendError */ = {
    status: 403,
    statusText: 'Not Logged In',
    message: 'You must be logged in to perform this action'
  };
  currentUser: any;
  authenticatedUser!: Promise<any>;

  singleUseRegistrationPassword!: string;

  public loggedIn: ReplaySubject<any> = new ReplaySubject(1);

  readonly SYNC_ON_LOGOUT_TIMEOUT = 10000;

  async login(username: string, password: string): Promise<any> {
    try {
      if (Capacitor.isNativePlatform()) {
        this.currentUser = await signIn({ username, password });
      } else {
        this.currentUser = await signIn({
          username,
          options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP',
          }
        });
      }
    } catch (error: any) {
      if (this.isInternalErrorException(error)) {
        error.message = oneLine`Sorry, we can't log you in
        right now due to an unavailable service. We're
        aware of the problem and working on a solution.
        Please try again later.`;
      }
      throw error;
    }
  }

  refreshAuth(data: { redirectTo: string }) {
    this.refreshAuth$.next(data);
  }

  async isApproved(): Promise<ApplicationState> {
    const session = await this.getCurrentSession();
    const jwtClaims: any = session?.tokens?.idToken?.payload || {};
    const groups = jwtClaims['cognito:groups'] || [];
    return (groups.length > 0 && groups.indexOf('public') === -1) ? ApplicationState.APPROVED : ApplicationState.AUTHENTICATED;
  }

  async getCurrentUser(): Promise<AuthUser> {
    try {
      if (!this.authenticatedUser) {
        this.authenticatedUser = getCurrentUser();
      }
      const current = await this.authenticatedUser;
      const session = await this.getCurrentSession();
      if (current) {
        const jwtClaims = session.tokens?.idToken?.payload || {};
        const tokenIssuedAt: number = jwtClaims.iat!;
        const expiration: number = jwtClaims.exp!;

        // Force a refresh when the token is older than a day
        const forceRefresh = moment.unix(tokenIssuedAt).add(1, 'days').valueOf() <= moment().valueOf() && this.network.isOnline();
        const isExpired = moment.unix(expiration).valueOf() < moment().valueOf();
        const groups = (Array.isArray(jwtClaims['cognito:groups']) ? jwtClaims['cognito:groups'] as string[] : []);
        const isApproved = groups.indexOf('Registered') === -1 && groups.indexOf('public') === -1;
        if ((!this.network.isOnline() || session.tokens) && isApproved && !forceRefresh && !isExpired) {
          return current;
        } else {
          await this.forceRefreshToken();
        }
      }
    } catch (err: any) {
      console.log('Not authenticated yet, waiting', `sss${err}sss`, err.message);
    }
    this.authenticatedUser = getCurrentUser();
    return this.authenticatedUser;
  }

  async forceRefreshToken() {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (error) {
      throw error;
    }
  }

  getCurrentSession(): Promise<AuthSession> {
    return fetchAuthSession();
  }

  async logout() {
    await signOut();
  }

  async getToken(): Promise<string | undefined> {
    let currentSession = await this.getCurrentSession();
    const tokenExpiry = currentSession.tokens?.idToken?.payload.exp;
    const isExpired = tokenExpiry ? moment.unix(tokenExpiry).valueOf() < moment().valueOf() : true;
    if (isExpired) {
      currentSession = await fetchAuthSession({ forceRefresh: true });
    }
    return currentSession.tokens?.idToken?.toString()
  }

  generateNewPassword(): string {
    return uuid();
  }

  registerUser(userData: ICognitoSignUpData): Promise<any> {
    this.singleUseRegistrationPassword = userData.password;
    return signUp({
      username: userData.phone_number,
      password: userData.password,
      options: {
        userAttributes: {
          phone_number: userData.phone_number,
          given_name: userData.given_name,
          family_name: userData.family_name,
          birthdate: userData.birthdate,
          gender: userData.gender,
          'custom:sitecode': userData['custom:sitecode']
        }
      }
    });
  }

  async verifySignUpCode(phone: string, code: string): Promise<any> {
    await confirmSignUp({ username: phone, confirmationCode: code });

    this.currentUser = await signIn({
      username: phone,
      password: this.singleUseRegistrationPassword
    });

    return this.currentUser;
  }

  resendSignUpCode(phone: string): Promise<any> {
    return resendSignUpCode({ username: phone })
      .then(() => this.login(phone, this.singleUseRegistrationPassword));
  }

  sendForgotPasswordCode(phone: string): Promise<any> {
    return resetPassword({ username: phone });
  }

  async sendCustomAuthSmsCode(phone: string): Promise<any> {
    const { isSignedIn, nextStep } = await signIn({
      username: phone,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
      }
    }).then((user) => {
      this.currentUser = user;
      return user;
    }).catch((error: any) => {
      console.log(error);
      return error;
    });
  }

  async submitVerifySMSCode(code: string): Promise<any> {
    const result = await confirmSignIn({ challengeResponse: code });

    if (!result?.isSignedIn) {
      throw new Error('Invalid code. Please try again.')
    }
  }

  submitForgotPassword(phone: string, code: string, newPassword: string): Promise<any> {
    return confirmResetPassword({
      username: phone,
      confirmationCode: code,
      newPassword,
    });
  }

  // More robust version with fallback to handle both v5 and v6 errors
  isUserNotFoundException(error: any) {
    return error && (error.name === 'UserNotFoundException' || error.code === 'UserNotFoundException');
  }

  isNotAuthorizedException(error: any) {
    return error && (error.name === 'NotAuthorizedException' || error.code === 'NotAuthorizedException');
  }

  isUserNotConfirmedException(error: any) {
    return error && (error.name === 'UserNotConfirmedException' || error.code === 'UserNotConfirmedException');
  }

  isUsernameExistsException(error: any) {
    return error && (error.name === 'UsernameExistsException' || error.code === 'UsernameExistsException');
  }

  // Invalid code errors
  isCodeMismatchException(error: any) {
    return error && (error.name === 'CodeMismatchException' || error.code === 'CodeMismatchException');
  }

  isExpiredCodeException(error: any) {
    return error && (error.name === 'ExpiredCodeException' || error.code === 'ExpiredCodeException');
  }

  // Serverside Errors start
  isInvalidParameterException(error: any) {
    return error && (error.name === 'InvalidParameterException' || error.code === 'InvalidParameterException');
  }

  isUserLambdaValidationException(error: any) {
    return error && (error.name === 'UserLambdaValidationException' || error.code === 'UserLambdaValidationException');
  }

  isUnexpectedLambdaException(error: any) {
    return error && (error.name === 'UnexpectedLambdaException' || error.code === 'UnexpectedLambdaException');
  }

  isInvalidLambdaResponseException(error: any) {
    return error && (error.name === 'InvalidLambdaResponseException' || error.code === 'InvalidLambdaResponseException');
  }

  isInternalErrorException(error: any) {
    return error && (error.name === 'InternalErrorException' || error.code === 'InternalErrorException');
  }

  isTooManyRequestsException(error: any) {
    return error && (error.name === 'TooManyRequestsException' || error.code === 'TooManyRequestsException');
  }

  isTooManyFailedAttemptsException(error: any) {
    return error && (error.name === 'TooManyFailedAttemptsException' || error.code === 'TooManyFailedAttemptsException');
  }

  isLimitExceededException(error: any) {
    return error && (error.name === 'LimitExceededException' || error.code === 'LimitExceededException');
  }
  // Serverside Errors end
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  amplifyAppService: AuthService;
  platform: Platform;

  constructor(inj: Injector) {
    this.amplifyAppService = inj.get(AuthService);
    this.platform = inj.get(Platform);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (request.url.startsWith(Environment.SYNC_URL) || request.url.startsWith(Environment.MOBILE_API) || request.url.startsWith(Environment.REFERRAL_URL) || request.url.startsWith(Environment.HAPI_FHIR_URL)) {

      return from(this.amplifyAppService.getCurrentSession())
        .pipe(
          mergeMap(session => {
            const headers = {
              Authorization: request.url.startsWith(Environment.HAPI_FHIR_URL) ? `Bearer ${session.tokens?.accessToken?.toString()}` : `Bearer ${session.tokens?.idToken?.toString()}`
            };
            request = request.clone({
              setHeaders: headers
            });
            return next.handle(request);
          }),
          catchError(error => {
            // We want only want to track errors specific to the interceptor here
            if (!(error instanceof HttpErrorResponse)) {
              if (!this.platform.is('pwa')) {
                // this.firebaseAnalytics.logEvent('http_interceptor_error', { error });
              }
              console.error(error);
            }
            throw error;
          })
        );
    } else {
      return next.handle(request);
    }
  }
}
